import React from 'react';
import PropTypes from 'prop-types';
import { unit } from 'mathjs';
import { format } from 'date-fns';

/**
 * Currency Shortener
 *
 * Takes high precision and large value currency data and converts
 * to a set of abbreviated forms.
 *
 */
var CurrencyShortener = function CurrencyShortener(currencyValue) {
  if (isNaN(currencyValue) || currencyValue === null) {
    return {
      unit: "",
      value: "N/A"
    };
  }

  if (currencyValue === 0 || currencyValue < 1000) {
    return {
      unit: "",
      value: Math.round(currencyValue)
    };
  }

  var unitMap = [{
    unit: "k",
    value: 1000
  }, {
    unit: "M",
    value: 1000000
  }, {
    unit: "B",
    value: 1000000000
  }, {
    unit: "T",
    value: 1000000000000
  }];
  var results = unitMap.map(function (item) {
    var value = currencyValue / item.value;
    return {
      value: value.toFixed(2),
      unit: item.unit
    };
  });
  return results.filter(function (t) {
    return t.value >= 1 && t.value < 1000;
  })[0];
};

/**
 * Energy Units Utility Module
 */

/**
 * Automatically determines the best fit for energy units
 * @param {*} value the number in wh
 */

var determineBestUnit = function determineBestUnit(value) {
  var units = ["Wh", "kWh", "MWh", "GWh", "TWh"];
  var converted = units.map(function (u) {
    return {
      unit: u,
      value: unit(value, "Wh").to(u).toNumber()
    };
  });

  var _ref = converted.filter(function (e) {
    return e.value > 1 && e.value < 1000;
  })[0] || {},
      unit$1 = _ref.unit;

  return unit$1 ? unit$1 : "kWh";
};
/**
 * auto formats the energy value based on the best unit
 * @param {*} value the number in wh
 */

var autoFormatEnergyValue = function autoFormatEnergyValue(value) {
  var unit$1 = determineBestUnit(value);
  var num = unit(value, "Wh").to(unit$1).toJSON();
  return "".concat(parseFloat(num.value).toFixed(2).toLocaleString(), "  ").concat(num.unit);
};

var BreakdownLabel = function BreakdownLabel(_ref) {
  var center = _ref.center,
      color = _ref.color,
      under = _ref.under,
      label = _ref.label,
      percentage = _ref.percentage,
      tarrif = _ref.tarrif,
      value = _ref.value;
  var height = under ? 55 : 5;
  var TextStart = height / 2 - 2;
  var startX = center > 75 ? center - 2 : center + 2;
  var polyPoints = "".concat(startX, " ").concat(height / 2, " ,").concat(center, " ").concat(height / 2, " , ").concat(center, " 12.5");
  return React.createElement(React.Fragment, null, React.createElement("polyline", {
    points: polyPoints,
    style: {
      fill: "none",
      stroke: color,
      strokeWidth: 0.5
    }
  }), React.createElement("text", {
    y: TextStart,
    x: center > 75 ? startX - 15 : startX + 1,
    fill: color,
    fontSize: 4,
    stroke: color,
    strokeWidth: 0.25
  }, parseFloat(percentage).toFixed(2), "%"), React.createElement("text", {
    y: TextStart + 2.5,
    x: center > 75 ? startX - 15 : startX + 1,
    fill: color,
    fontSize: 2,
    stroke: color,
    strokeWidth: 0.15
  }, label), React.createElement("text", {
    y: TextStart + 5,
    x: center > 75 ? startX - 15 : startX + 1,
    fill: color,
    fontSize: 2
  }, autoFormatEnergyValue(value)), React.createElement("text", {
    y: TextStart + 7.5,
    x: center > 75 ? startX - 15 : startX + 1,
    fill: color,
    fontSize: 2
  }, tarrif), ";");
};

var BreakdownBar = function BreakdownBar(_ref) {
  var colorConsumption = _ref.colorConsumption,
      colorExported = _ref.colorExported,
      colorSolarConsumed = _ref.colorSolarConsumed,
      colorEmpty = _ref.colorEmpty,
      gridConsumption = _ref.gridConsumption,
      exported = _ref.exported,
      solarConsumption = _ref.solarConsumption,
      segments = _ref.segments,
      energyPurchased = _ref.energyPurchased,
      solarEnergyConsumed = _ref.solarEnergyConsumed,
      energySold = _ref.energySold;
  var total = gridConsumption + exported + solarConsumption;

  var percentage = function percentage(num, total) {
    return total ? num / total * 100 : 0;
  };

  var aSegments = new Array(segments).fill("");
  return !(!gridConsumption && !exported && !solarConsumption) || total ? React.createElement("svg", {
    viewBox: "0 0 100 35",
    preserveAspectRatio: "xMidYMid meet"
  }, React.createElement("g", {
    transform: "scale(0.90) translate(5,5)"
  }, React.createElement("rect", {
    width: "".concat(percentage(solarConsumption, total), "%"),
    height: 10,
    x: 0,
    y: 10,
    fill: colorSolarConsumed
  }), solarConsumption && React.createElement(BreakdownLabel, {
    center: percentage(solarConsumption, total) / 2,
    color: colorSolarConsumed,
    under: false,
    label: "Self Consumption",
    percentage: percentage(solarConsumption, total),
    value: solarConsumption,
    tarrif: solarEnergyConsumed
  }), React.createElement("rect", {
    width: "".concat(percentage(exported, total), "%"),
    x: "".concat(percentage(solarConsumption, total), "%"),
    height: 10,
    y: 10,
    fill: colorExported
  }), exported && React.createElement(BreakdownLabel, {
    center: percentage(solarConsumption, total) + percentage(exported, total) / 2,
    color: colorExported,
    under: true,
    label: "Solar Exported",
    value: exported,
    percentage: percentage(exported, total),
    tarrif: energySold
  }), React.createElement("rect", {
    width: "".concat(percentage(gridConsumption, total), "%"),
    x: "".concat(100 - percentage(gridConsumption, total), "%"),
    height: 10,
    y: 10,
    fill: colorConsumption
  }), gridConsumption && React.createElement(BreakdownLabel, {
    center: 100 - percentage(gridConsumption, total) + percentage(gridConsumption, total) / 2,
    color: colorConsumption,
    under: false,
    value: gridConsumption,
    label: "Grid Consumption",
    percentage: percentage(gridConsumption, total),
    tarrif: energyPurchased
  }), aSegments.map(function (e, i) {
    return React.createElement("rect", {
      key: i,
      x: "".concat(i * (100 / segments), "%"),
      width: "".concat(100 / segments, "%"),
      height: 10,
      y: 10,
      strokeWidth: 100 / segments / 8,
      stroke: "#fff",
      fill: "none"
    });
  }))) : React.createElement("svg", {
    viewBox: "0 0 100 35",
    preserveAspectRatio: "xMidYMid meet"
  }, React.createElement("g", {
    transform: "scale(0.90) translate(5,5)"
  }, React.createElement("rect", {
    width: "100%",
    height: 10,
    x: 0,
    y: 10,
    fill: colorEmpty
  }), aSegments.map(function (e, i) {
    return React.createElement("rect", {
      key: i,
      x: "".concat(i * (100 / segments), "%"),
      width: "".concat(100 / segments, "%"),
      height: 10,
      y: 10,
      strokeWidth: 100 / segments / 8,
      stroke: "#fff",
      fill: "none"
    });
  })));
};

BreakdownBar.propTypes = {
  colorConsumption: PropTypes.string,
  gridConsumption: PropTypes.number,
  colorExported: PropTypes.string,
  exported: PropTypes.number,
  colorSolarConsumed: PropTypes.string,
  solarConsumption: PropTypes.number,
  colorEmpty: PropTypes.string,
  segments: PropTypes.number,
  energyPurchased: PropTypes.string,
  solarEnergyConsumed: PropTypes.string,
  energySold: PropTypes.string
};
BreakdownBar.defaultProps = {
  colorConsumption: "#808080",
  colorExported: "#EB7625",
  colorSolarConsumed: "#FFAC28",
  colorEmpty: "#ebebe4",
  gridConsumption: null,
  exported: null,
  solarConsumption: null,
  segments: 30
};

var EnergyBreakdown = function EnergyBreakdown(_ref) {
  var title = _ref.title,
      energy = _ref.energy,
      tariffs = _ref.tariffs,
      date = _ref.date;

  var formatTariffs = function formatTariffs() {
    var energyPurchasedValue = tariffs.energyPurchasedValue,
        solarEnergyConsumedValue = tariffs.solarEnergyConsumedValue,
        energySoldValue = tariffs.energySoldValue;
    var energyPurchased = CurrencyShortener(energyPurchasedValue);
    var solarConsumed = CurrencyShortener(solarEnergyConsumedValue);
    var energySold = CurrencyShortener(energySoldValue);
    return {
      energyPurchased: "(cost $".concat(energyPurchased.value).concat(energyPurchased.unit, ")"),
      solarEnergyConsumed: "(saving $".concat(solarConsumed.value).concat(solarConsumed.unit, ")"),
      energySold: "(earning $".concat(energySold.value).concat(energySold.unit, ")")
    };
  };

  var _ref2 = tariffs !== null ? formatTariffs() : {},
      energyPurchased = _ref2.energyPurchased,
      solarEnergyConsumed = _ref2.solarEnergyConsumed,
      energySold = _ref2.energySold;

  var _ref3 = energy !== null ? energy : {},
      energyGenerated = _ref3.energyGenerated,
      energyImported = _ref3.energyImported,
      energyExported = _ref3.energyExported;

  return React.createElement("div", null, React.createElement(BreakdownBar, {
    exported: energyImported,
    gridConsumption: energyExported,
    solarConsumption: energyGenerated,
    energyPurchased: energyPurchased,
    solarEnergyConsumed: solarEnergyConsumed,
    energySold: energySold
  }), React.createElement("p", {
    className: "w-100 text-center paragraph-small p-0 m-0"
  }, title, " for", " ", React.createElement("span", {
    className: "font-weight-bold"
  }, date ? date : format(new Date(), "MMM DD YYYY"))));
};

EnergyBreakdown.propTypes = {
  title: PropTypes.string,
  energy: PropTypes.object,
  tariffs: PropTypes.object,
  date: PropTypes.string
};
EnergyBreakdown.defaultProps = {
  title: "Energy breakdown",
  energy: null,
  tariffs: null,
  date: null
};

export default EnergyBreakdown;



//////////////////
// WEBPACK FOOTER
// ./node_modules/@solarschools/component-lib/components/EnergyBreakdown/index.js
// module id = 6lD4
// module chunks = 3